import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import { parkingQueueKey } from './useAPIGetParkingQueue'
import { ParkingSpotEquipment } from '../../../common/types'
import {
  JoinQueueBody,
  joinQueueBodySchema,
  parkingSpotResponseSchema,
} from '../../../common/validations/parkingSpotSchemas'

import { post } from 'api/apiClient'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'

const buildUrl = (contractUuid: string) =>
  API_ENDPOINTS.RESIDENT_CONTRACT_PARKING_QUEUE.replace(
    ':contractUuid',
    contractUuid
  )

export type JoinQueueInput = {
  equipmentWishes: ParkingSpotEquipment[]
  additionalInformation?: string
  costPoolId: number
}

export const useAPIJoinParkingQueue = () => {
  const qc = useQueryClient()
  const { currentContractId } = useCurrentContract()

  return useMutation({
    mutationKey: currentContractId
      ? (['contracts', currentContractId, 'parking-queue', 'join'] as const)
      : undefined,
    retry: false,
    mutationFn: async (rawBody: JoinQueueInput) => {
      const uniqueWishes = Array.from(
        new Set(rawBody.equipmentWishes)
      ) as ParkingSpotEquipment[]
      const body: JoinQueueBody = joinQueueBodySchema.parse({
        equipmentWishes: uniqueWishes,
        additionalInformation: (rawBody.additionalInformation ?? '').trim(),
        costPoolId: rawBody.costPoolId,
      })

      const url = buildUrl(currentContractId)
      return post(url, body, parkingSpotResponseSchema)
    },
    onSuccess: async () => {
      if (!currentContractId) return
      await qc.invalidateQueries({
        queryKey: parkingQueueKey(currentContractId),
      })
    },
  })
}
